import { Check, Clock, MapPin, Phone, ArrowRight } from "lucide-react";
import { BrowserMockup } from "../components/BrowserMockup";
import { Reveal, SectionLabel } from "../components/Reveal";
import { scrollToId } from "../lib/scroll";

// Immagini dimostrativi generiche (stock): nessun riferimento a clienti reali.
const IMG = {
  lidoHero:
    "https://images.unsplash.com/photo-1770703674662-ff4e5b987f3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHw0fHxzZWFzaWRlJTIwcmVzdGF1cmFudCUyMHRlcnJhY2UlMjBvY2VhbiUyMHZpZXclMjB0YWJsZSUyMGRpbmluZ3xlbnwwfHx8fDE3OTAzMzE4NDZ8MA&ixlib=rb-4.1.0&q=85",
  lidoDish:
    "https://images.pexels.com/photos/24289165/pexels-photo-24289165.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  lidoAtmosfera:
    "https://images.pexels.com/photos/36237503/pexels-photo-36237503.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  lidoTerrazza:
    "https://images.unsplash.com/photo-1782199600975-3cdc0b9117ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHszfHxzZWFzaWRlJTIwcmVzdGF1cmFudCUyMHRlcnJhY2UlMjBvY2VhbiUyMHZpZXclMjB0YWJsZSUyMGRpbmluZ3xlbnwwfHx8fDE3OTAzMzE4NDZ8MA&ixlib=rb-4.1.0&q=85",
  lidoPesce:
    "https://images.unsplash.com/photo-1621327017866-6fb07e6c96ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxnb3VybWV0JTIwc2VhZm9vZCUyMGRpc2glMjByZXN0YXVyYW50JTIwcGxhdGVkJTIwZmluZSUyMGRpbmluZ3xlbnwwfHx8fDE3OTAzMzE4NTN8MA&ixlib=rb-4.1.0&q=85",
  bnbCamera:
    "https://images.pexels.com/photos/14547138/pexels-photo-14547138.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  bnbDettaglio:
    "https://images.pexels.com/photos/15717924/pexels-photo-15717924.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  bnbInterno:
    "https://images.unsplash.com/photo-1731336478850-6bce7235e320?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHw0fHxib3V0aXF1ZSUyMGhvdGVsJTIwY296eSUyMGJlZHJvb20lMjBpbnRlcmlvciUyMGRlY29yfGVufDB8fHx8MTc9MDMzMTg0Nnww&ixlib=rb-4.1.0&q=85",
  barberHero:
    "https://images.pexels.com/photos/30547689/pexels-photo-30547689.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  barberInterno:
    "https://images.pexels.com/photos/3992850/pexels-photo-3992850.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  barberVintage:
    "https://images.unsplash.com/photo-1781925856343-c97d0d44f94c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxiYXJiZXJzaG9wJTIwaW50ZXJpb3IlMjBsZWF0aGVyJTIwY2hhaXIlMjB2aW50YWdlJTIwc2Fsb258ZW58MHx8fHwxNzkwMzMxODQ2fDA&ixlib=rb-4.1.0&q=85",
};

const ConceptBadge = () => (
  <span className="inline-block rounded-full border border-celeste/40 bg-celeste-soft px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-celeste-dark">
    Concept dimostrativo
  </span>
);

/* ---------- Concept principale: Ristorante / Lido ---------- */

const LidoDesktopSite = () => (
  <div className="flex h-full flex-col bg-white text-left" aria-hidden="true">
    {/* mini header del concept */}
    <div className="flex items-center justify-between border-b border-line px-4 py-2.5 sm:px-6">
      <span className="font-display text-xs font-extrabold tracking-wide text-navy-dark sm:text-sm">
        Osteria del Borgo
      </span>
      <div className="flex items-center gap-3">
        <span className="hidden gap-3 sm:flex">
          <span className="h-1.5 w-8 rounded-full bg-line" />
          <span className="h-1.5 w-8 rounded-full bg-line" />
          <span className="h-1.5 w-8 rounded-full bg-line" />
        </span>
        <span className="rounded-md bg-navy px-2.5 py-1 text-[9px] font-bold text-white sm:text-[10px]">
          Prenota un tavolo
        </span>
      </div>
    </div>
    {/* hero fotografica */}
    <div className="relative">
      <img
        src={IMG.lidoHero}
        alt=""
        loading="lazy"
        className="h-32 w-full object-cover sm:h-44 lg:h-52"
      />
      <div className="absolute inset-0 bg-navy-dark/45" />
      <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
        <span className="font-display text-sm font-extrabold text-white sm:text-xl">
          Cucina di mare sul lungomare
        </span>
        <span className="mt-1 hidden h-1.5 w-40 rounded-full bg-white/60 sm:block" />
        <span className="mt-2.5 rounded-full bg-celeste px-3.5 py-1.5 text-[9px] font-bold text-white sm:text-[10px]">
          Prenota il tuo tavolo
        </span>
      </div>
    </div>
    {/* menu */}
    <div className="grid grid-cols-3 gap-2.5 px-4 py-3.5 sm:gap-4 sm:px-6 sm:py-5">
      {[
        { img: IMG.lidoDish, label: "Antipasti di mare" },
        { img: IMG.lidoPesce, label: "Primi e secondi" },
        { img: IMG.lidoAtmosfera, label: "Il locale" },
      ].map((item) => (
        <div key={item.label} className="overflow-hidden rounded-lg border border-line">
          <img src={item.img} alt="" loading="lazy" className="h-12 w-full object-cover sm:h-16" />
          <div className="px-2 py-1.5">
            <span className="block truncate text-[8px] font-bold text-navy-dark sm:text-[10px]">
              {item.label}
            </span>
            <span className="mt-1 block h-1 w-4/5 rounded-full bg-line" />
          </div>
        </div>
      ))}
    </div>
    {/* galleria + info */}
    <div className="mt-auto border-t border-line bg-mist px-4 py-3 sm:px-6 sm:py-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex gap-2">
          {[IMG.lidoTerrazza, IMG.lidoAtmosfera, IMG.lidoDish].map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              loading="lazy"
              className="h-9 w-12 rounded-md object-cover sm:h-11 sm:w-16"
            />
          ))}
        </div>
        <div className="flex flex-col gap-1.5 text-[8px] font-semibold text-steel sm:text-[10px]">
          <span className="flex items-center gap-1.5">
            <Clock className="h-3 w-3 text-celeste-dark" /> 12:00–15:00 · 19:00–23:00
          </span>
          <span className="flex items-center gap-1.5">
            <MapPin className="h-3 w-3 text-celeste-dark" /> Lungomare
          </span>
          <span className="flex items-center gap-1.5">
            <Phone className="h-3 w-3 text-celeste-dark" /> Telefono e WhatsApp
          </span>
        </div>
      </div>
    </div>
  </div>
);

const LidoPhoneSite = () => (
  <div className="flex h-full flex-col bg-white" aria-hidden="true">
    <div className="flex items-center justify-between px-3 py-2">
      <span className="font-display text-[9px] font-extrabold text-navy-dark">Osteria del Borgo</span>
      <span className="h-1 w-4 rounded-full bg-line" />
    </div>
    <div className="relative">
      <img src={IMG.lidoHero} alt="" loading="lazy" className="h-20 w-full object-cover" />
      <div className="absolute inset-0 bg-navy-dark/45" />
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-[9px] font-extrabold text-white">Cucina di mare</span>
        <span className="mt-1.5 rounded-full bg-celeste px-2.5 py-1 text-[7px] font-bold text-white">
          Prenota un tavolo
        </span>
      </div>
    </div>
    <div className="space-y-1.5 px-3 py-2.5">
      <span className="block h-1.5 w-3/5 rounded-full bg-navy/60" />
      <span className="block h-1.5 w-4/5 rounded-full bg-line" />
      <span className="block h-1.5 w-2/5 rounded-full bg-line" />
    </div>
    <div className="mt-auto flex gap-1.5 px-3 pb-3">
      {[IMG.lidoDish, IMG.lidoTerrazza].map((src, i) => (
        <img key={i} src={src} alt="" loading="lazy" className="h-9 w-1/2 rounded-md object-cover" />
      ))}
    </div>
  </div>
);

/* ---------- Concept secondari ---------- */

const BnbSite = () => (
  <div className="flex h-full flex-col bg-white text-left" aria-hidden="true">
    <div className="flex items-center justify-between px-4 py-2.5">
      <span className="font-display text-[11px] font-extrabold tracking-wide text-navy sm:text-xs">
        B&B Villa Serena
      </span>
      <span className="rounded-full border border-navy/20 px-2.5 py-1 text-[8px] font-semibold text-navy sm:text-[9px]">
        Richiedi disponibilità
      </span>
    </div>
    <div className="relative">
      <img src={IMG.bnbCamera} alt="" loading="lazy" className="h-20 w-full object-cover sm:h-24" />
      <div className="absolute inset-0 bg-navy-dark/35" />
      <div className="absolute inset-0 flex flex-col justify-center px-4">
        <span className="font-display text-[10px] font-extrabold text-white sm:text-xs">
          Soggiorni curati, colazione inclusa
        </span>
        <span className="mt-1 block h-1 w-24 rounded-full bg-white/60" />
      </div>
    </div>
    <div className="grid flex-1 grid-cols-2 gap-2.5 p-3 sm:gap-3 sm:p-4">
      {[
        { img: IMG.bnbDettaglio, label: "Camera Matrimoniale" },
        { img: IMG.bnbInterno, label: "Camera Doppia" },
      ].map((room) => (
        <div key={room.label} className="overflow-hidden rounded-lg border border-line">
          <img src={room.img} alt="" loading="lazy" className="h-12 w-full object-cover sm:h-14" />
          <div className="px-2 py-1.5">
            <span className="block truncate text-[8px] font-bold text-navy sm:text-[9px]">{room.label}</span>
            <span className="mt-1 block h-1 w-3/5 rounded-full bg-line" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const BarberSite = () => (
  <div className="flex h-full flex-col bg-[#1B222B] text-left" aria-hidden="true">
    <div className="flex items-center justify-between px-4 py-2.5">
      <span className="font-display text-[10px] font-extrabold tracking-[0.14em] text-[#E8DDC8] sm:text-[11px]">
        BARBERIA CENTRALE
      </span>
      <span className="rounded-md bg-[#C9A25E] px-2.5 py-1 text-[8px] font-bold text-[#1B222B] sm:text-[9px]">
        Prenota
      </span>
    </div>
    <div className="relative">
      <img src={IMG.barberHero} alt="" loading="lazy" className="h-16 w-full object-cover sm:h-20" />
      <div className="absolute inset-0 bg-[#1B222B]/45" />
      <div className="absolute inset-0 flex items-center px-4">
        <span className="font-display text-[10px] font-extrabold text-[#E8DDC8] sm:text-xs">
          Stile classico, cura moderna
        </span>
      </div>
    </div>
    <div className="flex flex-1 gap-3 p-3 sm:p-4">
      <div className="flex-1 rounded-lg border border-white/10 bg-white/[0.04] p-2.5">
        <span className="block h-1 w-10 rounded-full bg-[#C9A25E]" />
        <div className="mt-2 space-y-1.5">
          {["Taglio", "Barba", "Taglio + Barba"].map((row) => (
            <div key={row} className="flex items-center justify-between border-b border-white/5 pb-1">
              <span className="text-[7px] font-semibold text-[#E8DDC8]/90 sm:text-[8px]">{row}</span>
              <span className="block h-1 w-5 rounded-full bg-[#E8DDC8]/30" />
            </div>
          ))}
        </div>
      </div>
      <div className="flex w-16 flex-col gap-1.5 sm:w-20">
        <img src={IMG.barberInterno} alt="" loading="lazy" className="h-1/2 w-full rounded-md object-cover" />
        <img src={IMG.barberVintage} alt="" loading="lazy" className="h-1/2 w-full rounded-md object-cover" />
      </div>
    </div>
  </div>
);

/* ---------- Sezione ---------- */

const FEATURED_INCLUDES = [
  "Presentazione dell'attività",
  "Menu o listino",
  "Galleria fotografica",
  "Orari e posizione",
  "Prenotazioni o richieste di informazioni",
];

const SECONDARY = [
  {
    key: "beb",
    category: "B&B",
    description:
      "Sito pensato per presentare struttura, camere e servizi e facilitare le richieste di disponibilità.",
    points: ["Camere e servizi", "Gallery", "Richiesta disponibilità"],
    Site: BnbSite,
    testid: "project-beb",
  },
  {
    key: "barber",
    category: "Barber Shop",
    description:
      "Sito pensato per mostrare servizi, stile dell'attività e rendere semplice la prenotazione di un appuntamento.",
    points: ["Servizi e prezzi", "Gallery", "Prenotazioni"],
    Site: BarberSite,
    testid: "project-barber",
  },
];

export const Portfolio = () => (
  <section id="progetti" aria-labelledby="progetti-title" className="bg-white">
    <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-28">
      <Reveal className="max-w-3xl">
        <SectionLabel>Progetti</SectionLabel>
        <h2 id="progetti-title" className="mt-4 font-display text-3xl font-bold tracking-tight text-navy-dark sm:text-4xl">
          Esempi di siti che possiamo realizzare.
        </h2>
        <p className="mt-5 text-base leading-relaxed text-steel sm:text-lg">
          Ogni progetto viene sviluppato su misura in base all'attività, agli obiettivi e alle
          funzionalità richieste. Questi concept dimostrativi mostrano alcune possibili soluzioni
          per settori diversi.
        </p>
      </Reveal>

      {/* Concept principale */}
      <Reveal className="mt-14">
        <article className="grid items-center gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <div className="relative pb-10 sm:pb-14">
              <BrowserMockup url="Anteprima progetto" testid="project-ristorante" contentClass="">
                <LidoDesktopSite />
              </BrowserMockup>
              <div
                data-testid="project-ristorante-phone"
                className="absolute -bottom-0 right-3 w-24 overflow-hidden rounded-[1.4rem] border-[5px] border-navy-dark bg-white shadow-[0_18px_45px_-15px_rgba(11,31,51,0.45)] sm:right-8 sm:w-36 sm:rounded-[1.8rem] sm:border-[7px]"
              >
                <div className="aspect-[9/17]">
                  <LidoPhoneSite />
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <ConceptBadge />
            <h3 className="mt-4 font-display text-2xl font-bold tracking-tight text-navy-dark sm:text-3xl">
              Ristorante / Lido
            </h3>
            <p className="mt-4 text-base leading-relaxed text-steel">
              Un concept pensato per attività della ristorazione e strutture stagionali, con
              l'obiettivo di presentare al meglio il locale, i servizi e rendere immediato il
              contatto con il cliente.
            </p>
            <p className="mt-7 text-xs font-semibold uppercase tracking-[0.2em] text-celeste-dark">
              Può includere
            </p>
            <ul className="mt-3 space-y-2.5">
              {FEATURED_INCLUDES.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-navy-dark sm:text-base">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-celeste" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </article>
      </Reveal>

      {/* Concept secondari */}
      <div className="mt-16 grid gap-10 md:grid-cols-2 md:gap-8 lg:gap-12">
        {SECONDARY.map((project, i) => (
          <Reveal key={project.key} delay={i * 0.1}>
            <article>
              <BrowserMockup url="Anteprima progetto" testid={project.testid} contentClass="aspect-[16/10]">
                <project.Site />
              </BrowserMockup>
              <div className="mt-5">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="font-display text-xl font-semibold text-navy-dark">{project.category}</h3>
                  <ConceptBadge />
                </div>
                <p className="mt-2.5 text-sm leading-relaxed text-steel sm:text-base">
                  {project.description}
                </p>
                <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
                  {project.points.map((point) => (
                    <li key={point} className="flex items-center gap-2 text-sm font-medium text-navy">
                      <span className="h-1.5 w-1.5 rounded-full bg-celeste" aria-hidden="true" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      {/* Chiusura */}
      <Reveal className="mt-16">
        <div className="flex flex-col items-center gap-6 rounded-2xl border border-line bg-mist px-7 py-10 text-center sm:px-12">
          <p className="max-w-2xl text-base leading-relaxed text-navy sm:text-lg">
            Ogni sito viene realizzato su misura e può essere adattato alle esigenze specifiche
            della tua attività.
          </p>
          <a
            href="#contatti"
            onClick={(e) => { e.preventDefault(); scrollToId("#contatti"); }}
            data-testid="portfolio-cta"
            className="group inline-flex items-center gap-2 rounded-lg bg-navy px-7 py-3.5 text-base font-semibold text-white shadow-md transition-all duration-200 hover:bg-navy-dark hover:shadow-lg active:scale-[0.98]"
          >
            Parlaci del tuo progetto
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);
